import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  MoreVert,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Bar } from 'react-chartjs-2';
import { useAuth } from '../../context/AuthContext';
// import { db } from '../../firebase/config';
// import { collection, query, where, getDocs } from 'firebase-shim';
import indexedDBService from '../../utils/indexedDBService';
import { Link, useNavigate } from 'react-router-dom';

// Import Chart.js components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface ClientReceivable {
  id: string;
  name: string;
  amount: number;
}

const ClientReceivablesWidget: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clientReceivables, setClientReceivables] = useState<ClientReceivable[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    fetchClientReceivables();
  }, [currentUser]);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRefresh = () => {
    handleMenuClose();
    fetchClientReceivables();
  };

  const handleViewAllClients = () => {
    handleMenuClose();
    navigate('/clients');
  };

  const fetchClientReceivables = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      // First get all clients
      // @ts-ignore
      const clients = await indexedDBService.getAllItems<any>('clients');

      // Then get all invoices
      // @ts-ignore
      const invoices = await indexedDBService.getAllItems<any>('invoices');

      // Calculate receivables per client
      const receivables: { [clientId: string]: number } = {};

      invoices.forEach(invoice => {
        const clientId = invoice.clientId;

        // Skip if no client ID or already paid/deleted
        if (!clientId) return;

        // Convert status to lowercase for case-insensitive comparison
        const status = (invoice.status || '').toLowerCase();

        // Only include invoices with pending/overdue status
        if (status === 'pending' || status === 'overdue') {
          const amount = Number(invoice.amount || invoice.totalAmount || 0);

          if (amount > 0) {
            // Add to client's total receivables
            receivables[clientId] = (receivables[clientId] || 0) + amount;
          }
        }
      });

      // Create array of client receivables with names
      const clientReceivablesList: ClientReceivable[] = clients
        .filter(client => receivables[client.id] > 0)
        .map(client => ({
          id: client.id,
          name: client.name,
          amount: receivables[client.id] || 0
        }));

      // Sort by amount (highest first) and get top 5
      clientReceivablesList.sort((a, b) => b.amount - a.amount);
      setClientReceivables(clientReceivablesList.slice(0, 5));

    } catch (error) {
      console.error('Error fetching client receivables:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format currency values
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Prepare chart data
  const chartData = {
    labels: clientReceivables.map(client => client.name),
    datasets: [
      {
        label: 'Amount Due',
        data: clientReceivables.map(client => client.amount),
        backgroundColor: [
          'rgba(71, 118, 230, 0.8)',
          'rgba(53, 162, 235, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 159, 64, 0.8)',
          'rgba(255, 99, 132, 0.8)',
        ],
        borderColor: [
          'rgba(71, 118, 230, 1)',
          'rgba(53, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(255, 99, 132, 1)',
        ],
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const, // Horizontal bar chart
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const client = clientReceivables[context.dataIndex];
            return `Amount: ${formatCurrency(client.amount)}`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: function (this: any, tickValue: any) {
            return formatCurrency(Number(tickValue));
          }
        },
        grid: {
          display: true,
        },
      },
      y: {
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <Card sx={{ borderRadius: 3, height: '100%' }}>
      <CardHeader
        title="Client Balances"
        subheader="Top 5 by amount"
        action={
          <IconButton onClick={handleMenuClick}>
            <MoreVert />
          </IconButton>
        }
      />
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleRefresh}>Refresh</MenuItem>
        <MenuItem onClick={handleViewAllClients}>View All Clients</MenuItem>
      </Menu>
      <CardContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <CircularProgress />
          </Box>
        ) : clientReceivables.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 300,
              textAlign: 'center'
            }}
          >
            <InfoIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No outstanding receivables found.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All your clients are paid up!
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ height: 300, mb: 2 }}>
              <Bar data={chartData} options={chartOptions} />
            </Box>
            {clientReceivables.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Legend:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {clientReceivables.map((client, index) => (
                    <Box key={client.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: chartData.datasets[0].backgroundColor[index % 5]
                        }}
                      />
                      <Typography variant="body2" noWrap>
                        {client.name}: {formatCurrency(client.amount)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Link to="/clients" style={{ textDecoration: 'none' }}>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ cursor: 'pointer' }}
                >
                  View All Clients →
                </Typography>
              </Link>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientReceivablesWidget; 
